/**
 * @zendir/ui - Dialog/Modal Component
 *
 * Modal dialog following Astro UX Design System.
 *
 * @example
 * ```tsx
 * <Dialog
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm Action"
 * >
 *   <p>Are you sure you want to proceed?</p>
 *   <Dialog.Actions>
 *     <Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button>
 *     <Button onClick={handleConfirm}>Confirm</Button>
 *   </Dialog.Actions>
 * </Dialog>
 * ```
 */

import React, { memo, useEffect, useRef, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../theme";
import { classNames } from "../../utils";

export type DialogSize = "small" | "medium" | "large" | "fullscreen";

export interface DialogProps {
  /** Open state */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Dialog title */
  title?: string;
  /** Size variant */
  size?: DialogSize;
  /** Show close button */
  showCloseButton?: boolean;
  /** Close on backdrop click */
  closeOnBackdropClick?: boolean;
  /** Close on escape key */
  closeOnEscape?: boolean;
  /** Children */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
}

export interface DialogActionsProps {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
}

const DialogActions = memo(function DialogActions({
  children,
  align = "right",
}: DialogActionsProps): React.ReactElement {
  const { tokens } = useTheme();

  return (
    <div
      style={{
        display: "flex",
        justifyContent:
          align === "left"
            ? "flex-start"
            : align === "right"
              ? "flex-end"
              : "center",
        gap: tokens.spacing.sm,
        marginTop: tokens.spacing.lg,
        paddingTop: tokens.spacing.md,
        borderTop: tokens.borders.divider,
      }}
    >
      {children}
    </div>
  );
});

export const Dialog = memo(function Dialog({
  open,
  onClose,
  title,
  size = "medium",
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  children,
  className = "",
}: DialogProps): React.ReactElement | null {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const DIALOG_Z_INDEX = 2147483000;

  const sizeConfig = {
    small: { width: "min(360px, calc(100vw - 32px))", maxHeight: "80vh" },
    medium: { width: "min(540px, calc(100vw - 32px))", maxHeight: "80vh" },
    large: { width: "min(800px, calc(100vw - 32px))", maxHeight: "90vh" },
    fullscreen: { width: "100vw", maxHeight: "100vh" },
  };
  const config = sizeConfig[size];

  // Handle escape key. Kept in a ref so the focus-trap effect below can
  // depend on `open` ALONE: if the effect re-ran whenever `onClose` changed
  // identity (e.g. a parent passing an inline arrow that re-renders on a
  // status poll), it would re-fire the focus() call on every render and pull
  // focus out of whatever the user was typing — making inputs untypable.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === "Escape") {
        onClose();
      }
    },
    [closeOnEscape, onClose],
  );
  const handleKeyDownRef = useRef(handleKeyDown);
  handleKeyDownRef.current = handleKeyDown;

  // Focus trap and escape key — runs only on open/close transitions.
  useEffect(() => {
    if (!open) return undefined;

    previousActiveElement.current = document.activeElement as HTMLElement;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => handleKeyDownRef.current(e);
    document.addEventListener("keydown", onKeyDown);

    // Move focus into the dialog once — but honour a child that already
    // grabbed it (e.g. an Input with autoFocus). Without this guard we'd
    // override the intended initial focus and land on the first focusable,
    // which is usually the close button.
    const focusTimer = setTimeout(() => {
      if (dialogRef.current?.contains(document.activeElement)) return;
      const focusable = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 0);

    return () => {
      clearTimeout(focusTimer);
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
      previousActiveElement.current?.focus();
    };
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={classNames("zendir-dialog-backdrop", className)}
      onClick={closeOnBackdropClick ? onClose : undefined}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: isTransparentTheme
          ? `${tokens.colors.background.base}66`
          : `${tokens.colors.background.base}99`,
        backdropFilter: isTransparentTheme ? "blur(4px)" : undefined,
        WebkitBackdropFilter: isTransparentTheme ? "blur(4px)" : undefined,
        display: "flex",
        alignItems: size === "fullscreen" ? "stretch" : "center",
        justifyContent: "center",
        zIndex: DIALOG_Z_INDEX,
        animation: `zendir-dialog-backdrop ${tokens.animation.duration.normal}ms ${tokens.animation.easing.default} both`,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        className="zendir-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: config.width,
          maxWidth: size === "fullscreen" ? "100vw" : "calc(100vw - 32px)",
          maxHeight: config.maxHeight,
          backgroundColor: isTransparentTheme
            ? `${tokens.colors.accent.primary}14`
            : tokens.colors.background.surface,
          ...(isTransparentTheme && {
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: `1px solid ${tokens.colors.accent.primary}33`,
          }),
          borderRadius: size === "fullscreen" ? 0 : tokens.borderRadius.lg,
          boxShadow: tokens.shadows.xl,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          animation: `zendir-dialog-panel ${tokens.animation.duration.normal}ms ${tokens.animation.easing.default} both`,
        }}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: `${tokens.spacing.md} ${tokens.spacing.lg}`,
              borderBottom: tokens.borders.divider,
            }}
          >
            {title && (
              <h2
                id={titleId}
                style={{
                  margin: 0,
                  fontSize: tokens.typography.fontSize.lg,
                  fontWeight: tokens.typography.fontWeight.semibold,
                  color: tokens.colors.text.primary,
                }}
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close dialog"
                style={{
                  padding: tokens.spacing.sm,
                  minWidth: 44,
                  minHeight: 44,
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: tokens.borderRadius.sm,
                  cursor: "pointer",
                  color:
                    tokens.colors.text.muted ?? tokens.colors.text.tertiary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: `color ${tokens.animation.fast}`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = tokens.colors.text.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color =
                    tokens.colors.text.muted ?? tokens.colors.text.tertiary;
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            padding: tokens.spacing.lg,
            overflow: "auto",
          }}
        >
          {children}
        </div>
      </div>

      <style>
        {`
          @keyframes zendir-dialog-backdrop { from { opacity: 0; } to { opacity: 1; } }
          @keyframes zendir-dialog-panel { from { transform: scale(0.96); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        `}
      </style>
    </div>,
    document.body,
  );
}) as React.NamedExoticComponent<DialogProps> & {
  Actions: typeof DialogActions;
};

// Attach subcomponent
(Dialog as any).Actions = DialogActions;

export default Dialog;
